import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { FadeUp } from "@/components/Reveal";
import { IMAGES, JOURNEY, PIPELINE } from "@/data/content";
import SEO from "@/components/SEO";

const Research = () => (
  <>
    <SEO
      title="Research & Innovation"
      description="DSIR-recognised in-house R&D — molecular breeding labs, multi-location trials and breeder-led innovation behind every Seed Park hybrid."
    />
    <PageHero
      eyebrow="Research & Innovation"
      titleLines={["Where genetics", "becomes yield."]}
      description="DSIR-approved R&D research farms, molecular breeding laboratories and a breeder-led culture — the scientific engine behind every Seed Park hybrid."
      image={IMAGES.lab1}
    />

    <section data-testid="dsir-recognition-section" className="py-24 lg:py-28">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-12 lg:px-10">
        <FadeUp className="lg:col-span-4">
          <div className="mx-auto flex max-w-xs items-center justify-center border border-[#123C6B]/10 bg-white p-8 shadow-[0_24px_60px_rgba(18,60,107,0.12)]">
            <img src="/assets/dsir-logo.png" alt="DSIR Recognized — In-House R&D, Department of Scientific & Industrial Research, Govt. of India" className="w-full" data-testid="dsir-badge" />
          </div>
        </FadeUp>
        <div className="lg:col-span-8">
          <FadeUp delay={0.1}>
            <p className="mb-4 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-brand-400">
              <span className="inline-block h-px w-8 bg-brand-400" /> Government Recognition
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <h2 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
              Backed by DSIR-recognized R&amp;D labs.
            </h2>
          </FadeUp>
          <FadeUp delay={0.25}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-mist">
              Seed Park's research programme is formally recognized by the Department of Scientific &amp;
              Industrial Research (DSIR), Ministry of Science &amp; Technology, Government of India — a
              distinction held by a select group of seed companies. It certifies that our breeding farms,
              molecular laboratories and evaluation protocols meet national standards for industrial
              research, and it keeps our scientists accountable to the highest benchmark in the country.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>

    <section data-testid="research-chapters" className="py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="The Seed Park Method"
          titleLines={["Eight chapters,", "one discipline."]}
          description="A single unbroken chain from research to farmer — every chapter measured, documented and accountable."
        />
        <div className="grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {JOURNEY.map((ch, i) => (
            <FadeUp key={ch.num} delay={(i % 4) * 0.08} className="h-full">
              <div className="group flex h-full flex-col bg-forest-950 p-8 transition-colors hover:bg-forest-900" data-testid={`research-chapter-${ch.num}`}>
                <p className="font-display text-5xl font-black text-outline transition-colors group-hover:text-brand-400 group-hover:[-webkit-text-stroke:0]">{ch.num}</p>
                <h3 className="mt-6 font-display text-xl font-bold tracking-tight">{ch.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-sage">{ch.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="research-pipeline" className="bg-forest-900 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="Hybrid Development Pipeline"
          titleLines={["Five years. Five gates.", "Zero shortcuts."]}
        />
        <div className="grid gap-5 md:grid-cols-5">
          {PIPELINE.map((p, i) => (
            <FadeUp key={p.title} delay={i * 0.08} className="h-full">
              <div className="relative flex h-full flex-col border border-white/10 bg-forest-950 p-6" data-testid={`research-pipeline-step-${i}`}>
                <p className="font-display text-4xl font-black text-brand-400/30">0{i + 1}</p>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-400">{p.step}</p>
                <h3 className="mt-2 font-display text-lg font-bold leading-snug tracking-tight">{p.title}</h3>
                <p className="mt-3 text-xs leading-relaxed text-sage">{p.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="research-tech" className="py-28 lg:py-36">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-6">
          <SectionHeading
            eyebrow="Laboratory & Technology"
            titleLines={["Marker-assisted.", "Data-driven.", "Field-proven."]}
            description="Our molecular breeding centre applies marker-assisted selection, doubled-haploid speed breeding and genomic prediction — compressing breeding cycles while raising the bar on selection accuracy."
          />
          <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10">
            {[
              ["MAS", "Marker-assisted selection across all crop programs"],
              ["DH", "Doubled-haploid lines for rapid inbred fixation"],
              ["GP", "Genomic prediction models trained on trial data"],
              ["Phenomics", "High-throughput field phenotyping at R&D farms"],
            ].map(([t, d]) => (
              <FadeUp key={t}>
                <div className="h-full bg-forest-950 p-6">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-brand-400">{t}</p>
                  <p className="mt-2 text-xs leading-relaxed text-mist">{d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6">
          <FadeUp delay={0.1}>
            <div className="relative overflow-hidden border border-white/10">
              <img src={IMAGES.lab2} alt="Molecular breeding laboratory" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-950/70 to-transparent" />
              <p className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-400">Molecular Breeding Centre, Hyderabad</p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  </>
);

export default Research;
